/**
 * Canonical entity-type label helper.
 *
 * Single entry-point for resolving the human-readable name of a CRM entity type.
 * Lookup order:
 *   1. TODO: ahoy_type_label formula evaluation (future schema-driven labels)
 *      See https://github.com/AhoyAI/ahoy/issues/PLACEHOLDER
 *   2. Schema API: label / label_plural from the object-types API response
 *      (for custom entity types the org administrator named themselves)
 *   3. Built-in display-name registry (mirrors entityTypeRegistry.ts without
 *      pulling in React component dependencies)
 *   4. snake-case-to-title-case fallback (strips ahoy_ / org_ prefixes)
 */

import type { CrmObject } from '@/types/crm';

import type { CrmObjectTypeSchema } from '../core/SchemaClient';
import { pluralizeEntityLabel } from './entityTypeUtils';

/**
 * Lightweight display-name map for all known built-in entity types.
 *
 * Mirrors the displayName / pluralDisplayName fields in entityTypeRegistry.ts
 * without importing that file's heavy React-component dependencies.
 * Keep in sync with entityTypeRegistry.ts when adding new entity types.
 */
const BUILTIN_DISPLAY_NAMES: Record<
  string,
  { singular: string; plural: string }
> = {
  ahoy_contact: { singular: 'Contact', plural: 'Contacts' },
  ahoy_company: { singular: 'Company', plural: 'Companies' },
  ahoy_deal: { singular: 'Deal', plural: 'Deals' },
  ahoy_note: { singular: 'Note', plural: 'Notes' },
  ahoy_task: { singular: 'Task', plural: 'Tasks' },
  ahoy_meeting: { singular: 'Meeting', plural: 'Meetings' },
  ahoy_meeting_notes: {
    singular: 'Meeting Summary',
    plural: 'Meeting Summaries',
  },
  ahoy_user: { singular: 'User', plural: 'Users' },
  ahoy_import: { singular: 'Import', plural: 'Imports' },
  ahoy_risk: { singular: 'Risk', plural: 'Risks' },
  ahoy_research: { singular: 'Research', plural: 'Research' },
  ahoy_email_thread: {
    singular: 'Email Thread',
    plural: 'Email Threads',
  },
  ahoy_event_automation: {
    singular: 'Automation',
    plural: 'Automations',
  },
  ahoy_scheduled_automation: {
    singular: 'Scheduled Automation',
    plural: 'Scheduled Automations',
  },
  ahoy_automation_webhook: {
    singular: 'Inbound Webhook',
    plural: 'Inbound Webhooks',
  },
  ahoy_web_visit: { singular: 'Visit', plural: 'Visits' },
  ahoy_web_visitor: { singular: 'Visitor', plural: 'Visitors' },
  ahoy_web_form: { singular: 'Web Form', plural: 'Web Forms' },
  ahoy_booking_type: { singular: 'Booking Link', plural: 'Booking Links' },
  ahoy_booking: { singular: 'Booking', plural: 'Bookings' },
  ahoy_enrichment_request: {
    singular: 'Enrichment Request',
    plural: 'Enrichment Requests',
  },
  ahoy_deal_pipeline: { singular: 'Pipeline', plural: 'Pipelines' },
  ahoy_deal_pipeline_stage: { singular: 'Stage', plural: 'Stages' },
  ahoy_contact_lifecycle_stage: {
    singular: 'Lifecycle Stage',
    plural: 'Lifecycle Stages',
  },
  ahoy_file: { singular: 'File', plural: 'Files' },
  ahoy_audit: { singular: 'Audit', plural: 'Audits' },
  ahoy_property_type: { singular: 'Property', plural: 'Properties' },
  ahoy_org: { singular: 'Org', plural: 'Orgs' },
  ahoy_external_integration: {
    singular: 'Integration',
    plural: 'Integrations',
  },
};

/** Strip ahoy_ / org_ prefix and convert snake_case to Title Case. */
function snakeCaseToTitleCase(str: string): string {
  return str
    .replace(/^ahoy_/, '')
    .replace(/^org_/, '')
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export interface TypeLabelOptions {
  plural?: boolean;
}

/**
 * Get a human-readable label for a CRM entity type name.
 *
 * Pass `objectTypes` (from the object-types API) to enable tier-2 schema
 * lookup — required for custom org-defined entity types to display the
 * administrator's chosen label rather than the snake-case fallback.
 *
 * @example
 * getTypeLabelByName('ahoy_contact')          // 'Contact'
 * getTypeLabelByName('ahoy_contact', { plural: true }) // 'Contacts'
 * getTypeLabelByName('org_role', {}, objectTypes) // 'Role' (from schema API)
 */
export function getTypeLabelByName(
  entityTypeName: string,
  options: TypeLabelOptions = {},
  objectTypes: CrmObjectTypeSchema[] = []
): string {
  const { plural = false } = options;

  // Reject `*_timeline` identifiers up front. These are timeline-event type
  // names (e.g. `ahoy_meeting_timeline`), not entity/object types — the
  // snake-case fallback below would otherwise dress them up as plausible
  // entity labels ("Meeting Timeline") and quietly mislead callers.
  if (entityTypeName.endsWith('_timeline')) {
    return 'Unknown Object';
  }

  // Tier 1: ahoy_type_label formula evaluation — stubbed, see issue above.

  // Tier 2: Schema API (custom entity types named by the org admin)
  if (objectTypes.length > 0) {
    const schema = objectTypes.find(o => o.entity_type_name === entityTypeName);
    if (schema) {
      return plural ? schema.label_plural : schema.label;
    }
  }

  // Tier 3: Built-in registry
  const entry = BUILTIN_DISPLAY_NAMES[entityTypeName];
  if (entry) {
    return plural ? entry.plural : entry.singular;
  }

  // Tier 4: snake-case fallback
  const singularLabel = snakeCaseToTitleCase(entityTypeName);
  return plural ? pluralizeEntityLabel(singularLabel) : singularLabel;
}

/**
 * Get a human-readable label for a CRM object's entity type.
 *
 * Prefer this over `getTypeLabelByName` when you have the full object
 * available — future tier-1 formula evaluation will need it.
 *
 * @example
 * getTypeLabel(contactObject)                    // 'Contact'
 * getTypeLabel(contactObject, { plural: true })  // 'Contacts'
 * getTypeLabel(noteObject)                       // 'Note'
 */
export function getTypeLabel(
  object: CrmObject,
  options: TypeLabelOptions = {},
  objectTypes: CrmObjectTypeSchema[] = []
): string {
  return getTypeLabelByName(object.entity_type_name, options, objectTypes);
}
